import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchEntityListAction } from "../../actions/entityListActions";
import { updateEntityAction } from "../../actions/updateEntityAction";
import { approveEntityAction } from "../../actions/approveEntityAction";
import { deficiencyEntityAction } from "../../actions/deficiencyEntityAction";
import { getRequiredDocuments } from "./AllDataFields";
import { usePermissions } from "../../utils/roleUtils";

// helper: detect if string looks like an email
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// helper: truncate text to maxLen chars with ellipsis
const truncate = (text, maxLen = 10) =>
  text && text.length > maxLen ? text.slice(0, maxLen) + "..." : text || "—";

// Entity category options
export const entityCategoryOptions = [
  { value: "", label: "Entity Category" },
  { value: "client", label: "Client" },
  { value: "Service Partner", label: "Service Partner" },
];

// Document status options
export const documentStatusOptions = [
  { value: "", label: "All Documents Status" },
  { value: "pending", label: "Pending" },
  { value: "Done", label: "Done" },
];

// Approve status options
export const approveStatusOptions = [
  { value: "", label: "Approval Status" },
  { value: "false", label: "Pending " },
  { value: "true", label: "Approved" },
];

// ── Document status badge helper ───────────────────────────────────────────────
const DocStatusBadge = ({ documents = [] }) => {
  const hasAnyUploaded = documents.some((d) =>
    d.files?.some((f) => f.isUploaded),
  );
  if (hasAnyUploaded) return <span className="status-badge done">Done</span>;
  return <span className="status-badge pending">Pending</span>;
};

// ── Document list inline (shown when expanded) ─────────────────────────────────
export const DocListInline = ({ documents = [], entityData = {} }) => {
  const required = getRequiredDocuments(entityData);
  if (required.length === 0) return <span className="doc-none">—</span>;

  return (
    <div className="doc-list-inline">
      {required.map((req) => {
        const uploaded = documents.some(
          (d) => d.key === req.key && d.files?.some((f) => f.isUploaded),
        );
        return (
          <span
            key={req.key}
            className={`doc-chip ${uploaded ? "doc-chip--done" : "doc-chip--pending"}`}
          >
            {uploaded ? "✓" : "○"} {req.label}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Table column configuration — based on AllDataFields primary fields
 */
export const entityListColumns = [
  {
    header: "Company",
    accessor: "parentCode",
    render: (row) => row.entity?.parentCode || row.entity?.parentName || "—",
  },
  {
    header: "Entity Name",
    accessor: "entityName",
    render: (row) => (
      <div>
        <div className="col-primary">
          {row.entity?.entityName || row.entity?.parentName || "—"}
        </div>
        <div className="col-sub">{row.entity?.tradeName || ""}</div>
      </div>
    ),
  },
  {
    header: "Category / LOB",
    accessor: "entityCategory",
    render: (row) => {
      const cat = row.entity?.entityCategory || "";
      const catLabel =
        cat === "client"
          ? "Client"
          : cat === "Service Partner"
            ? "Service Partner"
            : cat || "—";
      return (
        <div>
          <div className="col-primary">{catLabel}</div>
          <div className="col-sub">{row.entity?.lobType || ""}</div>
        </div>
      );
    },
  },

  {
    header: "Contact",
    accessor: "emailAddress",
    render: (row) => (
      <div>
        <div className="col-primary" title={row.entity?.entityName || ""}>
          {truncate(row.entity?.entityName, 14)}
        </div>
        <div className="col-sub" title={row.entity?.emailAddress || ""}>
          {truncate(row.entity?.emailAddress, 14)}
        </div>
      </div>
    ),
  },
  {
    header: "Documents",
    accessor: "documentsStatus",
    render: (row) => <DocStatusBadge documents={row.documents || []} />,
  },
  {
    header: "Status",
    accessor: "vendorStatus",
    render: (row) => {
      const status = row.entity?.isApproved ? "Approved" : "Pending";
      return (
        <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>
      );
    },
  },
];

/**
 * Custom hook for managing entity list state and operations with server-side pagination
 */
export const useEntityList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initialCategory = urlParams.get("entityCategory") || "";
  const initialDocStatus = urlParams.get("documentStatus") || "";
  const initialApprove = urlParams.get("approveStatus") || "";

  const permissions = usePermissions();
  // Finance role defaults to showing only approved entities
  const roleDefaultApprove = permissions.defaultApproveStatus || "";
  const resolvedInitialApprove = initialApprove || roleDefaultApprove;

  const [currentPage, setCurrentPage] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [entities, setEntities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [entityCategory, setEntityCategory] = useState(initialCategory);
  const [documentStatus, setDocumentStatus] = useState(initialDocStatus);
  const [approveStatus, setApproveStatus] = useState(resolvedInitialApprove);
  const [pageSize, setPageSize] = useState(10);
  const debounceTimer = useRef(null);
  const isFirstRender = useRef(true);
  const prevSearchRef = useRef("");
  const isLoadingRef = useRef(false); // guard against concurrent calls

  // Build filters from current state
  const buildFilters = useCallback((query, category, status, approved) => {
    const filters = {};
    if (query) {
      if (isEmail(query)) {
        filters.emailAddress = query.trim();
      } else {
        filters.entityName = query.trim();
      }
    }
    if (category) filters.entityCategory = category;
    if (status) filters.status = status;
    // isVerified: null when no filter, true = Approved, false = Pending Approval
    filters.isVerified =
      approved === "true" ? true : approved === "false" ? false : null;
    return filters;
  }, []);

  const loadEntityList = useCallback(
    async (
      page = 1,
      query = "",
      category = "",
      status = "",
      approved = "",
      size = 10,
    ) => {
      if (isLoadingRef.current) {
        console.log("[EntityList] Already loading — skipped duplicate call");
        return;
      }
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const filters = buildFilters(query, category, status, approved);
        const response = await fetchEntityListAction(page, size, filters);

        if (response.success) {
          const data = response.data || [];
          const serverPagination = response.pagination || {};
          const correctedPagination = {
            ...serverPagination,
            totalItems: serverPagination.totalItems || data.length,
            totalPages:
              serverPagination.totalPages ||
              Math.ceil((serverPagination.totalItems || data.length) / size),
            hasNextPage:
              serverPagination.hasNextPage !== undefined
                ? serverPagination.hasNextPage
                : page < (serverPagination.totalPages || 1),
            hasPreviousPage:
              serverPagination.hasPreviousPage !== undefined
                ? serverPagination.hasPreviousPage
                : page > 1,
          };
          setEntities(data);
          setPagination(correctedPagination);
        } else {
          toast.error(response.message || "Failed to load entity list");
          setEntities([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            pageSize: size,
            hasNextPage: false,
            hasPreviousPage: false,
          });
        }
      } catch (error) {
        toast.error("Failed to load entity list");
        setEntities([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          pageSize: size,
          hasNextPage: false,
          hasPreviousPage: false,
        });
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [buildFilters],
  );

  // Single effect handles initial load + all filter/page changes.
  // Search is debounced 400ms; dropdown changes and page changes are immediate.
  useEffect(() => {
    let cancelled = false;

    const isSearchChange =
      !isFirstRender.current && prevSearchRef.current !== searchQuery;
    const delay = isSearchChange ? 400 : 0;
    prevSearchRef.current = searchQuery;
    isFirstRender.current = false;

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (!cancelled) {
        loadEntityList(
          currentPage,
          searchQuery,
          entityCategory,
          documentStatus,
          approveStatus,
          pageSize,
        );
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer.current);
    };
  }, [
    currentPage,
    searchQuery,
    entityCategory,
    documentStatus,
    approveStatus,
    pageSize,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page change
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1) {
      setCurrentPage(page);
      // effect will fire automatically via currentPage dep
    }
  };

  // Filter setters — reset page to 1 so effect fires with correct page
  const handleSearchChange = (value) => {
    setCurrentPage(1);
    setSearchQuery(value);
  };

  const handleCategoryChange = (value) => {
    setCurrentPage(1);
    setEntityCategory(value);
  };

  const handleStatusChange = (value) => {
    setCurrentPage(1);
    setDocumentStatus(value);
  };

  const handleApproveStatusChange = (value) => {
    setCurrentPage(1);
    setApproveStatus(value);
  };

  const handleEdit = (entityRow, viewOnly = false) => {
    setSelectedClient({ ...entityRow, viewOnly });
    setIsEditModalOpen(true);
  };

  const handleSaveClient = async (updatedData) => {
    try {
      const response = await updateEntityAction(updatedData.id, updatedData);
      if (response.success) {
        toast.success("Entity updated successfully!");
        await loadEntityList(currentPage);
        return response;
      } else {
        toast.error(response.message || "Failed to update entity");
        throw new Error(response.message);
      }
    } catch (error) {
      if (!error.message || error.message === "Failed to update entity") {
        toast.error("Failed to update entity");
      }
      throw error;
    }
  };

  const handleApproveClient = async (entityPrimaryId) => {
    try {
      const response = await approveEntityAction(entityPrimaryId);
      if (response.success) {
        toast.success(response.message || "Entity approved!");
        await loadEntityList(currentPage);
      } else {
        toast.error(response.message || "Failed to approve entity");
      }
      return response;
    } catch (error) {
      toast.error("Failed to approve entity");
      throw error;
    }
  };

  const handleDeficiencyClient = async ({
    entityPrimaryId,
    entityId,
    emailAddress,
    spocEmail,
    deficiencies,
  }) => {
    try {
      const response = await deficiencyEntityAction({
        entityPrimaryId,
        entityId,
        emailAddress,
        spocEmail,
        deficiencies,
      });
      if (response.success) {
        toast.success(response.message || "Deficiency submitted!");
        await loadEntityList(currentPage);
      } else {
        toast.error(response.message || "Failed to submit deficiency");
      }
      return response;
    } catch (error) {
      toast.error("Failed to submit deficiency");
      throw error;
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedClient(null);
  };

  const handleDelete = (entityRow) => {
    const entityName = entityRow.entity?.entityName || "this entity";
    if (window.confirm(`Are you sure you want to delete ${entityName}?`)) {
      toast.info("Delete functionality coming soon");
    }
  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setSearchQuery("");
    setEntityCategory("");
    setDocumentStatus("");
    setApproveStatus("");
    setPageSize(10);
  };

  // Load more — increments pageSize by 10 and re-fetches
  const handleLoadMore = () => setPageSize((prev) => prev + 10);
  const handleLoadLess = () => setPageSize((prev) => Math.max(10, prev - 10));

  return {
    currentPage,
    handleClearFilter,
    setCurrentPage: handlePageChange,
    isEditModalOpen,
    selectedClient,
    entities,
    isLoading,
    itemsPerPage: pageSize,
    pageSize,
    handleLoadMore,
    handleLoadLess,
    pagination,
    handleEdit,
    handleSaveClient,
    handleApproveClient,
    handleDeficiencyClient,
    handleCloseModal,
    handleDelete,
    totalPages: pagination.totalPages,
    currentEntities: entities,
    searchQuery,
    setSearchQuery: handleSearchChange,
    entityCategory,
    setEntityCategory: handleCategoryChange,
    documentStatus,
    setDocumentStatus: handleStatusChange,
    approveStatus,
    setApproveStatus: handleApproveStatusChange,
  };
};
